<template>
  <section id="section-addon" v-if="addons?.length > 0">
    <SectionHeading id="toc-addon" :icon="Icon" title="Available add-ons" />

    <div class="addon" v-for="addon in addons">
      <div class="content">
        <ItineraryTimelineDay
          v-if="addon.day"
          class="subtitle"
          :date="tripStartsAt"
          :day="addon.day"
          :title="addon.dayTitle"
          short
        />
        <div class="title">{{ addon.title }}</div>
        <div class="description" v-html="addon.descriptionHtml"></div>
        <div class="price">{{ currencyFormat(addon.priceCents, baseCurrency, true) }}</div>
        <div
          class="available"
          :class="{
            soldout: addon.available === 0 && !addon.cancelled,
            cancelled: addon.cancelled,
          }"
        >
          <template v-if="addon.cancelled">{{ t('cancelled') }}</template>
          <template v-else>{{ t('spots_left', { count: addon.available }) }}</template>
        </div>
      </div>

      <div class="images" v-if="addon.images?.length > 0">
        <MediaGallery :feature-images="addon.images" layout="single" />
      </div>
    </div>
  </section>
</template>

<script setup>
import { useItineraryStore } from '@/stores/itinerary.js'
import SectionHeading from '@/modules/Itinerary/Components/SectionHeading.vue'
import Icon from 'app/assets/images/itinerary/pin_add.svg?raw'
import { currencyFormat } from '@/utils/numberUtils.js'
import { useShared } from '@/helpers/vueHelpers.js'
import MediaGallery from '@/modules/MediaGallery/MediaGallery.vue'
import ItineraryTimelineDay from '@/modules/Itinerary/Components/ItineraryTimelineDay.vue'

const { t } = useShared()
const store = useItineraryStore()
const {
  data: {
    addons,
    itinerary: { baseCurrency },
    tripStartsAt,
  },
} = store
store.monitorVisibility('addon', '#section-addon')
</script>

<style lang="scss" scoped>
.addon {
  display: grid;
  grid-template-columns: 4fr 1fr;
  padding-top: 24px;
  gap: 24px;

  &:not(:last-child) {
    padding-bottom: 24px;
    border-bottom: 1px solid $gray-lighter;
  }

  @include mobile {
    grid-template-columns: 1fr;
  }
}

.content > *:not(:last-child) {
  margin-bottom: 16px;
}

.title {
  font-weight: 700;
}

.description :deep() {
  @include markdown-display;
}

.price {
  font-weight: 600;
  color: $brand-primary;
}

.available {
  color: $brand-tertiary;
  font-weight: 600;

  &.soldout {
    color: $brand-secondary;
  }

  &.cancelled {
    color: $brand-secondary;
  }
}

.images {
  img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 15px;
  }
}

.subtitle {
  color: $gray;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  text-transform: uppercase;
}
</style>

<i18n lang="yaml">
en:
  spots_left: 'Sold out | {count} spot left | {count} spots left'
  cancelled: 'Cancelled'
</i18n>
